import { useEffect, useRef, useState } from "react";
import { apiGet } from "../api/http";
import { parseApiError } from "../utils/apiErrors";
import { formatDateTimeForTimeZone } from "../utils/dateTime";

const DEFAULT_POLL_INTERVAL_MS = 10 * 60 * 1000;

/**
 * HealthStatus
 *
 * Modes:
 *   footerMode  – compact pill: dot + label + HH:MM timestamp (no refresh button)
 *   default     – admin widget: dot + label + "last checked" line + refresh button
 *
 * Usage:
 *   <HealthStatus />                          // admin widget (full)
 *   <HealthStatus footerMode />               // footer pill (minimal)
 *   <HealthStatus compact />                  // inline compact (no label detail)
 */
const HealthStatus = ({
  endpoint = "/api/health",
  label = "Backend health",
  pollIntervalMs,
  compact = false,
  footerMode = false,
}) => {
  const intervalMs =
    Number(process.env.REACT_APP_ADMIN_HEALTH_POLL_INTERVAL_MS) ||
    pollIntervalMs ||
    DEFAULT_POLL_INTERVAL_MS;

  const [healthStatus, setHealthStatus] = useState("checking");
  const [healthError, setHealthError] = useState("");
  const [lastChecked, setLastChecked] = useState(null);
  const [refreshing, setRefreshing] = useState(false);

  // Avoid stale-closure issue: use a ref for lastChecked inside the async fn
  const lastCheckedRef = useRef(null);

  const loadHealthStatus = async ({ manual = false } = {}) => {
    if (manual) setRefreshing(true);
    try {
      const { data } = await apiGet(endpoint);
      const status =
        typeof data?.status === "string" ? data.status.toLowerCase() : "unknown";
      setHealthStatus(status === "ok" ? "ok" : status);
      setHealthError("");
      const now = new Date().toISOString();
      setLastChecked(now);
      lastCheckedRef.current = now;
    } catch (err) {
      setHealthStatus("offline");
      setHealthError(parseApiError(err, "Unable to reach backend"));
      const now = new Date().toISOString();
      setLastChecked(now);
      lastCheckedRef.current = now;
    } finally {
      if (manual) setRefreshing(false);
    }
  };

  useEffect(() => {
    loadHealthStatus();
    const timer = setInterval(loadHealthStatus, intervalMs);
    return () => clearInterval(timer);
  }, [endpoint, intervalMs]);

  const isOnline = healthStatus === "ok";
  const isChecking = healthStatus === "checking";

  const dotColor = isOnline ? "#16a34a" : isChecking ? "#94a3b8" : "#dc2626";
  const statusLabel = isOnline ? "Live" : isChecking ? "Checking…" : "Offline";
  const detailText = isOnline
    ? "Backend is healthy"
    : healthError || "Unable to reach backend";

  const shortTime = lastChecked
    ? formatDateTimeForTimeZone(lastChecked, undefined, { hour: "2-digit", minute: "2-digit" })
    : null;

  const longTime = lastChecked
    ? formatDateTimeForTimeZone(lastChecked, undefined, { hour: "2-digit", minute: "2-digit", second: "2-digit" })
    : null;

  // ─── Footer pill ────────────────────────────────────────────────────────────
  if (footerMode) {
    return (
      <div
        className="health-status-footer"
        style={{
          display: "flex",
          alignItems: "center",
          gap: 6,
          whiteSpace: "nowrap",
          fontSize: "0.8rem",
        }}
      >
        <span
          style={{
            width: 7,
            height: 7,
            borderRadius: "50%",
            background: dotColor,
            display: "inline-block",
            flexShrink: 0,
          }}
        />
        <span style={{ fontWeight: 600, color: "var(--text-high)" }}>
          {statusLabel}
        </span>
        {shortTime ? (
          <span style={{ color: "var(--text-muted)" }}>{shortTime}</span>
        ) : null}
      </div>
    );
  }

  // ─── Admin / default widget ─────────────────────────────────────────────────
  return (
    <div
      className={`health-status-widget${compact ? " health-status-widget--compact" : ""}`}
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: compact ? 8 : 10,
        flexWrap: "nowrap",
        whiteSpace: "nowrap",
        minWidth: 0,
        background: "var(--surface, #f8fafc)",
        border: "1px solid var(--border, #e2e8f0)",
        borderRadius: 8,
        padding: compact ? "5px 10px" : "6px 12px",
      }}
    >
      {/* Status dot */}
      <span
        style={{
          width: compact ? 8 : 9,
          height: compact ? 8 : 9,
          borderRadius: "50%",
          background: dotColor,
          display: "inline-block",
          flexShrink: 0,
        }}
      />

      {/* Label + status */}
      <div style={{ minWidth: 0 }}>
        {!compact && (
          <div
            style={{
              fontSize: "0.7rem",
              textTransform: "uppercase",
              letterSpacing: "0.06em",
              color: "var(--text-muted)",
              lineHeight: 1,
              marginBottom: 2,
            }}
          >
            {label}
          </div>
        )}
        <div
          style={{
            fontWeight: 600,
            fontSize: compact ? "0.82rem" : "0.88rem",
            color: isOnline ? "#15803d" : isChecking ? "var(--text-muted)" : "#dc2626",
            lineHeight: 1.2,
          }}
        >
          {statusLabel}
        </div>
      </div>

      {/* Last checked time */}
      {longTime && (
        <span
          style={{
            color: "var(--text-muted)",
            fontSize: "0.78rem",
            borderLeft: "1px solid var(--border, #e2e8f0)",
            paddingLeft: compact ? 8 : 10,
          }}
        >
          {longTime}
        </span>
      )}

      {/* Refresh button */}
      <button
        type="button"
        onClick={() => loadHealthStatus({ manual: true })}
        disabled={refreshing}
        aria-label="Refresh backend status"
        title="Refresh"
        style={{
          background: "none",
          border: "none",
          cursor: refreshing ? "default" : "pointer",
          padding: "2px 4px",
          color: "var(--text-muted)",
          fontSize: "0.95rem",
          lineHeight: 1,
          opacity: refreshing ? 0.5 : 1,
          display: "flex",
          alignItems: "center",
          borderLeft: "1px solid var(--border, #e2e8f0)",
          paddingLeft: compact ? 8 : 10,
          marginLeft: 0,
          transition: "opacity 0.15s",
        }}
      >
        {refreshing ? (
          <span
            style={{
              display: "inline-block",
              width: 12,
              height: 12,
              border: "2px solid var(--text-muted)",
              borderTopColor: "transparent",
              borderRadius: "50%",
              animation: "spin 0.7s linear infinite",
            }}
          />
        ) : (
          "⟳"
        )}
      </button>

      {/* Error tooltip-ish text for non-compact full mode */}
      {!compact && !isOnline && !isChecking && healthError ? (
        <span
          style={{
            color: "#dc2626",
            fontSize: "0.78rem",
            maxWidth: 200,
            overflow: "hidden",
            textOverflow: "ellipsis",
            borderLeft: "1px solid var(--border, #e2e8f0)",
            paddingLeft: 10,
          }}
        >
          {detailText}
        </span>
      ) : null}
    </div>
  );
};

export default HealthStatus;